import { useCallback, useEffect, useRef, useState } from "react";
import { listFloridaCounties, lookupTax, formatRatePercent } from "../../api/tax";

// Florida sales-tax county picker: auto-detects the county from an address and
// lets the user override it. Shared across the admin booking + quote flows.
// (Extracted from the retired customer booking wizard.)
export default function TaxCountyPicker({ address, county, rateBasisPoints, autoDetected, onChange }) {
  const [counties, setCounties] = useState([]);
  const [showOverride, setShowOverride] = useState(Boolean(county));
  const [loading, setLoading] = useState(false);
  const lastAddress = useRef("");
  const manuallyChosen = useRef(Boolean(county) && !autoDetected);

  useEffect(() => {
    let active = true;
    listFloridaCounties()
      .then((res) => {
        if (!active) return;
        setCounties(res.counties || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const runLookup = useCallback(async () => {
    if (!address || !address.trim()) return;
    if (manuallyChosen.current) return;
    if (lastAddress.current === address) return;
    lastAddress.current = address;
    setLoading(true);
    try {
      const result = await lookupTax({ address });
      if (result.county) {
        onChange({
          county: result.county,
          rateBasisPoints: result.rate_basis_points,
          autoDetected: Boolean(result.guessed),
        });
      }
    } catch {
      /* silently ignore */
    } finally {
      setLoading(false);
    }
  }, [address, onChange]);

  useEffect(() => {
    const t = setTimeout(runLookup, 500);
    return () => clearTimeout(t);
  }, [runLookup]);

  function handleCountyChange(newCounty) {
    manuallyChosen.current = true;
    const match = counties.find((c) => c.name === newCounty);
    onChange({
      county: newCounty,
      rateBasisPoints: match ? match.rate_basis_points : null,
      autoDetected: false,
    });
  }

  const detectedLine =
    county && autoDetected
      ? `Detected from your address: ${county} County · ${formatRatePercent(rateBasisPoints)}`
      : county
      ? `${county} County · ${formatRatePercent(rateBasisPoints)}`
      : loading
      ? "Looking up your county..."
      : "We'll detect the county once you enter an address.";

  return (
    <div className="rounded-xl border border-birdie-green/15 bg-birdie-cream p-3 sm:p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-birdie-charcoal font-body">
          <span className="font-semibold">Florida sales tax:</span>{" "}
          <span className={county ? "text-birdie-green" : "text-birdie-charcoal/60"}>
            {detectedLine}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowOverride((v) => !v)}
          className="text-xs sm:text-sm font-semibold text-birdie-green hover:underline"
        >
          {showOverride ? "Hide" : county ? "Change" : "Pick county"}
        </button>
      </div>

      {showOverride && (
        <div className="mt-3">
          <label className="block text-xs font-semibold text-birdie-charcoal mb-1">County</label>
          <select
            value={county || ""}
            onChange={(e) => handleCountyChange(e.target.value)}
            className="w-full px-3 py-2 border border-birdie-green/20 rounded-lg focus:ring-2 focus:ring-birdie-mustard/40 focus:border-birdie-green text-sm"
          >
            <option value="">Select a county...</option>
            {counties.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} — {formatRatePercent(c.rate_basis_points)}
              </option>
            ))}
          </select>
          <p className="text-xs text-birdie-charcoal/60 mt-1">
            Tax is calculated on the final price (after any discount).
          </p>
        </div>
      )}
    </div>
  );
}

// Backwards-compatible named export (callers used `{ TaxCountyPicker }`).
export { TaxCountyPicker };
